import { useForm } from 'react-hook-form';

import { z } from 'zod';

import { showPromiseToast } from '@/lib/toast';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { useGetAvailableETLsQuery, useRunETLMutation } from '@/store/api/etlApi';

import { zodResolver } from '@hookform/resolvers/zod';

const runETLSchema = z.object({
  etl_type: z.string().min(1, 'Please select an ETL'),
  file_name: z.string().optional(),
});

type RunETLFormData = z.infer<typeof runETLSchema>;

interface RunETLModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: string;
  environment: string;
}

export const RunETLModal = ({ open, onOpenChange, client, environment }: RunETLModalProps) => {
  const { data: availableETLs, isLoading: isLoadingETLs } = useGetAvailableETLsQuery();
  const [runETL] = useRunETLMutation();

  const form = useForm<RunETLFormData>({
    resolver: zodResolver(runETLSchema),
    defaultValues: {
      etl_type: '',
      file_name: '',
    },
  });

  const selectedETLType = form.watch('etl_type');
  const selectedETL = availableETLs?.etls.find((etl) => etl.key === selectedETLType);
  const requiresFilename = selectedETL?.requires_filename ?? false;

  const onSubmit = async (data: RunETLFormData) => {
    if (requiresFilename && !data.file_name) {
      form.setError('file_name', {
        message: 'File name is required for this ETL',
      });
      return;
    }

    await showPromiseToast(
      runETL({
        client_name: client,
        environment: environment,
        etl_type: data.etl_type as any,
        file_name: data.file_name || 'placeholder',
      }).unwrap(),
      {
        loading: `Running ${selectedETL?.display_name || 'ETL'}...`,
        success: (data) => data.message,
        error: (error: any) => error?.data?.detail || error?.message || 'Failed to run ETL',
      },
    );

    // Reset form and close modal
    form.reset();
    onOpenChange(false);
  };

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <DialogTitle>Run ETL</DialogTitle>
          <DialogDescription>
            Run an ETL job for <span className='font-semibold'>{client}</span> •{' '}
            <span className='font-semibold'>{environment}</span>
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4 py-4'>
            {/* ETL Type Selector */}
            <FormField
              control={form.control}
              name='etl_type'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ETL Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder='Select an ETL to run' />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {isLoadingETLs ? (
                        <SelectItem value='loading' disabled>
                          Loading ETLs...
                        </SelectItem>
                      ) : (
                        availableETLs?.etls.map((etl) => (
                          <SelectItem key={etl.key} value={etl.key}>
                            {etl.display_name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* File Name Input (conditional) */}
            {requiresFilename && (
              <FormField
                control={form.control}
                name='file_name'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>File Name</FormLabel>
                    <FormControl>
                      <Input placeholder='Enter the file name on S3' {...field} />
                    </FormControl>
                    <FormDescription>
                      The client file name as it appears in the S3 bucket
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Info Message */}
            <div className='rounded-md bg-blue-50 p-3 border border-blue-200'>
              <p className='text-xs text-blue-700'>
                The ETL will be executed on the bastion host. You will receive an email notification
                when the job completes.
              </p>
            </div>

            {/* Actions */}
            <div className='flex justify-end gap-2'>
              <Button
                type='button'
                variant='outline'
                onClick={handleCancel}
                disabled={form.formState.isSubmitting}
              >
                Cancel
              </Button>
              <Button type='submit' disabled={form.formState.isSubmitting || !selectedETLType}>
                {form.formState.isSubmitting ? 'Running...' : 'Run ETL'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
